import { styled } from '@/stitches.config';
import Player from '@vimeo/player';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { VideoStoreContext } from './hooks/use-video-store';
import { createVideoStore } from './video-store';

interface VideoProps {
  children: ReactNode;
  videoId: string;
  aspectRatio: number | 'native';
  autoplay: boolean;
  loop: boolean;
  muted: boolean;
}

export function Video({
  children,
  videoId,
  aspectRatio,
  autoplay,
  loop,
  muted,
}: VideoProps) {
  const [store] = useState(() => createVideoStore(autoplay, muted));
  const [player, setPlayer] = useState<Player | null>(null);

  const [videoAspectRatio, setVideoAspectRatio] = useState(() => {
    if (aspectRatio === 'native') {
      return null;
    }

    return aspectRatio;
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!playerContainerRef.current) return;

    const player = new Player(playerContainerRef.current, {
      // TODO: This should be a number in the first place, now Sanity returns
      // a string
      id: Number(videoId),
      controls: false,
      responsive: true,
      autoplay: autoplay,
      loop: true,
      playsinline: true,
      background: loop,
      dnt: true,
      muted: true,
    });

    setPlayer(player);

    store.setState({ player, root: rootRef.current });
  }, [videoId, loop, autoplay, store]);

  // Listen to player events
  useEffect(() => {
    if (!player) return;

    player.on('loaded', () => {
      store.getState().onLoaded(player);

      if (aspectRatio === 'native') {
        Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(
          ([width, height]) => {
            setVideoAspectRatio(width / height);
          }
        );
      }
    });

    player.on('fullscreenchange', ({ fullscreen }) => {
      if (!fullscreen && isMobile) {
        store.getState().togglePlaying();
      }

      store.setState({ isFullscreen: fullscreen });
    });

    player.on('bufferstart', () => store.setState({ isBuffering: true }));
    player.on('bufferend', () => store.setState({ isBuffering: false }));

    return () => {
      player.destroy();
    };
  }, [player, aspectRatio, store]);

  return (
    <VideoStoreContext.Provider value={store}>
      <Root
        ref={rootRef}
        css={{
          paddingTop: videoAspectRatio ? `${100 / videoAspectRatio}%` : 0,
        }}
      >
        <PlayerContainer ref={playerContainerRef} />
        {children}
      </Root>
    </VideoStoreContext.Provider>
  );
}

const Root = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'black',
});

const PlayerContainer = styled('div', {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,

  '*:fullscreen > & > div': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
});
